import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaExclamationCircle, FaTrash } from 'react-icons/fa';
import TabbedFormComponent from '../../common/TabbedFormComponent';
import AdvancedDataTableComponent from '../../common/AdvancedDataTableComponent';
import FloatingLabelInput from '../../ui/FloatingLabelInput';
import FloatingLabelSelect from '../../ui/FloatingLabelSelect';
import { fetchClientData } from '../../../services/adminApiService';
import { registerSubClient, updateSubClient, fetchSubClientData, fetchSubClientDataById } from '../../../services/client/subClientApiService'; // Adjust the path as necessary


const SubClientForm = ({ selectedSubClient, onSubClientUpdated }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [formValues, setFormValues] = useState({
        subClientId: '',
        clientId: '',
        subClientName: '',
        legalName: '',
        address: '',
        contactPerson: '',
        contactNumber: '',
        email: ''
        
    });
    const [errors, setErrors] = useState({});

    const inputClasses = (field) =>
        errors[field] ? 'border-red-500' : 'border-gray-300';

    useEffect(() => {
        const getClients = async () => {
            try {
                const clientData = await fetchClientData();
                setClients(clientData.map(client => ({ value: client.clientId, label: client.clientName })));
            } catch (error) {
                console.error("Error fetching clients:", error);
            }
        };
        getClients();
    }, []);

    const handleClientChange = (option) => {
        setFormValues({ ...formValues, clientId: option.value });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
    };

    

    const validate = () => {
        const newErrors = {};

        // Validate main form fields
        if (!formValues.clientId) newErrors.clientId = 'Client is required';
        if (!formValues.subClientName) newErrors.subClientName = 'Sub Client Name is required';
        if (!formValues.legalName) newErrors.legalName = 'Leagal Name is required';
        if (!formValues.address) newErrors.address = 'Address is required';
        if (!formValues.contactPerson) newErrors.contactPerson = 'Contact Person is required';
        if (!formValues.contactNumber) newErrors.contactNumber = 'Contact Number is required';
        if (!formValues.email) newErrors.email = 'Email is required';

        return newErrors;
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            if (selectedSubClient && selectedSubClient.subClientId != "") {
                setIsEditing(true);
                setFormValues({

                    subClientId: selectedSubClient.subClientId || '',
                    clientId: selectedSubClient.clientId || '',
                    subClientName: selectedSubClient.subClientName || '',
                    legalName: selectedSubClient.legalName || '',
                    address: selectedSubClient.address || '',
                    contactPerson: selectedSubClient.contactPerson || '',
                    contactNumber: selectedSubClient.contactNumber || '',
                    email: selectedSubClient.email || '',
                });
            } else {
                setIsEditing(false);
            }
        }, 500); // 3000ms = 3 seconds

        return () => clearTimeout(timer);
    }, [selectedSubClient, clients]); // Ensure to re-run when countries change


    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length === 0) {
            //console.log(formValues);
            try {
                //console.log('Form submitted', formValues);
                setLoading(true);
                let data;
                if (isEditing) {
                    data = await updateSubClient(formValues);
                } else {
                    data = await registerSubClient(formValues);
                }

                setApiResponse(data);
                if (!data.error) {
                    resetFormValues();
                    onSubClientUpdated(null);
                    setIsEditing(false);
                    //setClients([]);
                } else {
                    return;
                }
            } catch (err) {
                const validationErrors = {};
                err.inner.forEach(error => {
                    validationErrors[error.path] = error.message;
                });
                setErrors(validationErrors);
                setApiResponse({ err: 'Failed to submit form. Please try again.' });
            } finally {
                setLoading(false);
            }
            setErrors({});
        } else {
            setErrors(validationErrors);
        }
    };

    const resetFormValues = () => {
        setFormValues({
            subClientId: '',
            clientId: '',
            subClientName: '',
            legalName: '',
            address: '',
            contactPerson: '',
            contactNumber: '',
            email: ''
            });
    }

    return (
        <div className="container mx-auto p-6 bg-white rounded-lg border">
            <form onSubmit={handleSubmit} className="space-y-6 w-full">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <FloatingLabelSelect
                            label="Select Organization"
                            id="clientId"
                            name="clientId"
                            value={clients.find(client => client.value === formValues.clientId) || null}
                            onChange={handleClientChange}
                            options={clients}
                            className={errors.clientId ? 'border-red-500' : 'border-gray-300'}
                        />
                        {errors.clientId && <div className="text-red-500 text-sm">{errors.clientId}</div>}
                    </div>
                    <div>
                        <FloatingLabelInput
                            label="Client Name"
                            id="subClientName"
                            name="subClientName"
                            value={formValues.subClientName}
                            onChange={handleChange}
                            className={inputClasses('subClientName')}
                        />
                        {errors.subClientName && <div className="text-red-500 text-sm">{errors.subClientName}</div>}
                    </div>

                    <div>
                        <FloatingLabelInput
                            label="Legal Name"
                            id="legalName"
                            name="legalName"
                            value={formValues.legalName}
                            onChange={handleChange}
                            className={inputClasses('legalName')}
                        />
                        {errors.legalName && <div className="text-red-500 text-sm">{errors.legalName}</div>}
                    </div>
                    <div>
                        <FloatingLabelInput
                            label="Address"
                            id="address"
                            name="address"
                            value={formValues.address}
                            onChange={handleChange}
                            className={inputClasses('address')}
                        />
                        {errors.address && <div className="text-red-500 text-sm">{errors.address}</div>}
                    </div>
                    <div>
                        <FloatingLabelInput
                            label="Contact Person"
                            id="contactPerson"
                            name="contactPerson"
                            value={formValues.contactPerson}
                            onChange={handleChange}
                            className={inputClasses('contactPerson')}
                        />
                        {errors.contactPerson && <div className="text-red-500 text-sm">{errors.contactPerson}</div>}
                    </div>
                    <div>
                        <FloatingLabelInput
                            label="contactNumber"
                            id="contactNumber"
                            name="contactNumber"
                            value={formValues.contactNumber}
                            onChange={handleChange}
                            className={inputClasses('contactNumber')}
                        />
                        {errors.contactNumber && <div className="text-red-500 text-sm">{errors.contactNumber}</div>}
                    </div>
                    <div>
                        <FloatingLabelInput
                            label="Email"
                            id="email"
                            name="email"
                            value={formValues.email}
                            onChange={handleChange}
                            className={inputClasses('email')}
                        />
                        {errors.email && <div className="text-red-500 text-sm">{errors.email}</div>}
                    </div>
                    {/* Add other fields here if needed */}
                </div>
                

                <button type="submit" className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">
                    {loading ? 'Submitting...' : selectedSubClient ? 'Update' : 'Submit'}
                </button>
            </form>
            {apiResponse && (
                <motion.div
                    className={`mt-4 flex items-center ${apiResponse.error ? 'text-red-500' : 'text-green-500'}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {apiResponse.error ? (
                        <>
                            <FaExclamationCircle className="mr-2" />
                            {apiResponse.error}
                        </>
                    ) : (
                        <>
                            <FaCheckCircle className="mr-2" />
                            {apiResponse.success}
                        </>
                    )}
                </motion.div>
            )}
        </div>
    );
};

const SubClientListTable = ({ onSelectSubClient, switchToFormTab }) => {
    const tableHeaders = ["id", "Organization Name", "Client Name", "Legal Name", "CreatedAt"];

    const [list, setList] = useState([]);  // Store Clients

    const [loading, setLoading] = useState(false);

    // Fetch Clients when the component loads
    useEffect(() => {
        const getData = async (cleintId) => {
            try {
                const data = await fetchSubClientData(localStorage.getItem('clientID'));  // Fetch countries from service
                const options = data.map((obj) => ({
                    id: obj.subClientId,
                    subClientName: obj.subClientName,
                    legalName: obj.legalName,
                    address: obj.address,
                    contactPerson: obj.contactPerson,
                    contactNumber : obj.contactNumber,
                    email: obj.email,
                    clientId: obj.clientId,
                    clientName: obj.clientName,
                    createdAt: obj.createdAt
                }));
                setList(options);  // Set the countries as options for the select dropdown

            } catch (error) {
                console.error("Error fetching countries:", error);
            } finally {
                // setLoadingCountries(false);  // Stop loading spinner
            }
        };

        getData();
    }, []);


    // Mapping of headers to data keys
    const headerMapping = {
        "id": "id",
        "Organization Name": "clientName", // Maps to the data key 'clientName'
        "Client Name": "subClientName", // Assuming you have 'email' key in your data. If not, replace accordingly
        "Legal Name": "legalName",
        "CreatedAt": "createdAt" // Maps to the data key 'createdAt'
    };


    const handleEdit = async (rowObject) => {
        try {
            setLoading(true); // Show loading spinner

            // Fetch client data based on the row object (e.g., client ID)
            const subClient = await fetchSubClientDataById(rowObject.id);
            var subClientData = ({
                subClientId: subClient.subClientId,
                subClientName: subClient.subClientName,
                legalName: subClient.legalName,
                address: subClient.address,
                contactPerson: subClient.contactPerson,
                contactNumber : subClient.contactNumber,
                email: subClient.email,
                clientId: subClient.clientId,
                clientName: subClient.clientName,
                createdAt: subClient.createdAt
            });
            // Populate the form with the fetched client data
            onSelectSubClient(subClientData);

            // Switch to the form tab
            switchToFormTab();

        } catch (error) {
            console.error('Error fetching client data:', error);
        } finally {
            setLoading(false); // Hide loading spinner
        }
    };


    return (
        <div className="container mx-auto p-6 bg-white rounded-lg border">
            <AdvancedDataTableComponent
                headers={tableHeaders}
                data={list}
                headerMapping={headerMapping}
                onEdit={handleEdit}
                showActions={true}
            />
        </div>
    );

};


const TestPage = () => {

    const [selectedSubClient, setselectedSubClient] = useState(null); // Track selected client for editing
    const [activeTab, setActiveTab] = useState(0); // Track active tab

    const handleEdit = (subClient) => {
        setselectedSubClient(subClient);
        setActiveTab(0);
    };
    const handleSubClientUpdated = () => {
        setselectedSubClient(null);
    };
    return (<TabbedFormComponent
        formContent={(switchToFormTab) => (
            <SubClientForm
                selectedSubClient={selectedSubClient}
                onSubClientUpdated={handleSubClientUpdated}
                switchToFormTab={switchToFormTab}
            />
        )}
        dataTableComponent={(switchToFormTab) => (
            <SubClientListTable
                onSelectSubClient={setselectedSubClient}
                switchToFormTab={switchToFormTab}
            />
        )}
    />)
};

export default TestPage;

